import { CustomApiError, useCreateJoinPresaleSignUp } from "@birdwatch/client";
import { emailFieldSchema } from "@birdwatch/common";
import { Form, setFieldErrors, useAppForm } from "@birdwatch/ui/form";
import { toast } from "@birdwatch/ui/toast";
import { useSignIn, useUser } from "@clerk/clerk-react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { z } from "zod";

import { APP_ROUTERS } from "@/constants/app-routers";
import { useRefresh } from "@/features/shared/realtime-refresh";
import { SignUpSlider } from "@/features/shared/sign-up-slider";
import { TextField } from "@/fields";
import { PRESALE_EVENTS, useAnalytics } from "@/libs/analytics";
import { formatPersonName } from "@/libs/format-person-name";
import { usePresaleMembership } from "./_api/use-presale-membership";
import { PresaleFunnelLayout } from "./_components/funnel/presale-funnel-layout";
import { PresalePlanCard } from "./_components/funnel/presale-plan-card";
import { getPresaleAnalyticsProps } from "./_libs/presale-analytics-props";
import {
  keepCampaignSearch,
  type PresaleCampaignSearch,
} from "./_libs/presale-funnel";

const nameFieldSchema = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required.`)
    .transform((value) => formatPersonName(value));

const presaleAccountSchema = z.object({
  email: emailFieldSchema,
  firstName: nameFieldSchema("First name"),
  lastName: nameFieldSchema("Last name"),
});

/**
 * Step 1 — Create Account.
 *
 * `POST /join/presale-sign-up` creates the `User` row AND the Clerk user, then hands
 * back a short-lived Clerk sign-in ticket so a NEW buyer is signed in on the spot and
 * goes straight to the home step — no emailed code in the way.
 *
 * A ticket comes back only for an account that request just created. An email that
 * already has an account gets `signInToken: null` and falls back to the emailed code,
 * because signing someone in on an existing email alone would let anyone take over
 * any member's account by typing their address.
 */
export function PresaleSignUpPage({
  search,
}: {
  search: PresaleCampaignSearch;
}) {
  const navigate = useNavigate();
  const { isSignedIn } = useUser();
  const { isLoaded: isSignInLoaded, signIn, setActive } = useSignIn();
  const refresh = useRefresh();
  const { mutateAsync: signUp } = useCreateJoinPresaleSignUp({
    // The route announces the customer it created or found: account and user.
    onSuccess: ({ accountId, userId }) => {
      void refresh("account.changed", { accountId });
      void refresh("user.changed", { userId });
    },
  });
  const { data: membership, isLoading: isLoadingMembership } =
    usePresaleMembership(search.checkoutLinkId);
  const { sendEvent } = useAnalytics();

  // Already signed in — the account step is done; go add the home.
  useEffect(() => {
    if (!isSignedIn) return;
    navigate({
      to: APP_ROUTERS.PRESALE_PLACE,
      search: keepCampaignSearch(search),
      replace: true,
    });
  }, [isSignedIn]);

  /**
   * The funnel's entry event, once per mount and only once the plan read settles — the
   * PRICED version is the useful one, and `usePresaleMembership` reports `isLoading: false`
   * immediately when there is no `checkoutLinkId`, so a link-less visit still fires.
   *
   * The ref is not decoration: `refetchOnWindowFocus` is on globally, so without it a tab
   * switch re-fires this. The join twin (`features/join/sign-up/index.tsx`) has no guard and
   * double-counts on refocus — deliberately not copied.
   */
  const addToCartSentRef = useRef(false);
  useEffect(() => {
    if (isLoadingMembership || addToCartSentRef.current) return;
    addToCartSentRef.current = true;
    sendEvent(PRESALE_EVENTS.ADD_TO_CART, {
      ...getPresaleAnalyticsProps({ search, membership }),
      stepId: "add_to_cart",
      stepName: "Add to Cart",
    });
    // `sendEvent` is deliberately absent: the analytics context value is rebuilt on every
    // provider render, so including it would let an unrelated render re-arm this effect.
  }, [isLoadingMembership, membership?.planKey]);

  const form = useAppForm({
    defaultValues: { email: "", firstName: "", lastName: "" },
    validators: { onBlur: presaleAccountSchema },
    onSubmit: async ({ value }) => {
      const params = {
        email: value.email,
        firstName: formatPersonName(value.firstName),
        lastName: formatPersonName(value.lastName),
      };
      try {
        // The generated client throws on a non-2xx, so reaching the next line
        // means the account exists (new or returning).
        const response = await signUp(params);

        sendEvent(PRESALE_EVENTS.ACCOUNT_REGISTRATION, {
          ...getPresaleAnalyticsProps({ search, membership }),
          stepId: "account_registration",
          stepName: "Account Registration",
          email: params.email,
          firstName: params.firstName,
          lastName: params.lastName,
          // Presale-only: this endpoint SUCCEEDS for an email that already has an account
          // (and returns no ticket), unlike the join sign-up. A registration count must
          // therefore filter on this rather than on the event alone.
          isReturning: !response?.signInToken,
        });

        if (!signIn || !isSignInLoaded) {
          throw new Error("Sign in is not ready yet. Please try again.");
        }

        if (response?.signInToken) {
          const attempt = await signIn.create({
            strategy: "ticket",
            ticket: response.signInToken,
          });
          if (attempt?.status !== "complete") {
            throw new Error("We couldn't start your session.");
          }
          await setActive({ session: attempt.createdSessionId });
          navigate({
            to: APP_ROUTERS.PRESALE_PLACE,
            search: keepCampaignSearch(search),
            replace: true,
          });
          return;
        }

        // Returning buyer — the account predates this request, so prove the email.
        const attempt = await signIn.create({
          strategy: "email_code",
          identifier: params.email,
        });
        const emailFactor = attempt?.supportedFirstFactors?.find(
          (factor: { strategy: string }) => factor.strategy === "email_code",
        ) as { emailAddressId?: string } | undefined;

        navigate({
          to: APP_ROUTERS.PRESALE_VERIFY,
          search: {
            ...keepCampaignSearch(search),
            email: params.email,
            emailAddressId: emailFactor?.emailAddressId,
            firstName: response?.firstName ?? undefined,
          },
        });
      } catch (error) {
        const fieldErrors =
          error instanceof CustomApiError ? error.errors : undefined;
        const named = fieldErrors
          ? Object.keys(fieldErrors).filter((key) => key in form.state.values)
          : [];
        if (fieldErrors && named.length > 0) {
          setFieldErrors(form, fieldErrors);
          return;
        }
        toast.error({
          title:
            error instanceof Error && error.message.trim()
              ? error.message
              : "We couldn't start your account.",
        });
      }
    },
  });

  return (
    <PresaleFunnelLayout
      step="account"
      title="Reserve your Founding Membership"
      description="Just your name and email — we’ll set up your account and take you to the next step."
      aside={<SignUpSlider />}
    >
      <Form form={form} className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            name="firstName"
            label="First name"
            placeholder="e.g., Jon"
            autoCapitalize="words"
          />
          <TextField
            name="lastName"
            label="Last name"
            placeholder="e.g., Doe"
            autoCapitalize="words"
          />
        </div>
        <TextField
          name="email"
          label="Email address"
          type="email"
          placeholder="email@example.com"
        />
        {membership && (
          <PresalePlanCard
            membership={membership}
            discountCode={search.discountCode ?? ""}
          />
        )}
        <form.SubmitButton size="xl" className="w-full">
          Continue
        </form.SubmitButton>
      </Form>
    </PresaleFunnelLayout>
  );
}
